import { resolveBankWithAI } from './claudeVision';
import { getSettings, saveSettings } from './settings';
import { BANKS } from './vietqr';
import type { BankMappingSource } from '../types';

export interface ResolvedBank {
  bankBin: string;
  bankName: string;
  mappingSource: BankMappingSource;
  confidence: number;
}

export function normalizeBankName(value?: string): string {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/\b(ngan hang|thuong mai co phan|tmcp|ngan hang tmcp|viet nam|vn)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

const STATIC_ALIASES: Record<string, string> = {
  vietinbank: '970415',
  'cong thuong': '970415',
  ctg: '970415',
  vietcombank: '970436',
  'ngoai thuong': '970436',
  vcb: '970436',
  bidv: '970418',
  'dau tu va phat trien': '970418',
  agribank: '970405',
  'nong nghiep va phat trien nong thon': '970405',
  techcombank: '970407',
  tcb: '970407',
  sacombank: '970403',
  stb: '970403',
  'mb bank': '970422',
  mbbank: '970422',
  mb: '970422',
  vpbank: '970432',
  vpb: '970432',
  acb: '970416',
  tpbank: '970423',
  tpb: '970423',
  hdbank: '970437',
  hdb: '970437',
  ocb: '970448',
};

function bankByBin(bin?: string) {
  return BANKS.find((bank) => bank.bin === bin);
}

function deterministicBin(sourceBankName: string, aliases: Record<string, string>) {
  const normalized = normalizeBankName(sourceBankName);
  if (!normalized) return undefined;
  const exactBank = BANKS.find((bank) => normalizeBankName(bank.name) === normalized);
  if (exactBank) return exactBank.bin;
  const alias = aliases[normalized] || STATIC_ALIASES[normalized];
  if (bankByBin(alias)) return alias;
  const matches = Object.entries({ ...STATIC_ALIASES, ...aliases })
    .filter(([key]) => normalized.includes(key) || key.includes(normalized))
    .map(([, bin]) => bin)
    .filter((bin, index, all) => all.indexOf(bin) === index && !!bankByBin(bin));
  return matches.length === 1 ? matches[0] : undefined;
}

export function resolveBankNameDeterministically(
  sourceBankName: string,
  aliases: Record<string, string> = {},
): ResolvedBank | null {
  const bin = deterministicBin(sourceBankName, aliases);
  const bank = bankByBin(bin);
  if (!bank) return null;
  const normalized = normalizeBankName(sourceBankName);
  return {
    bankBin: bank.bin,
    bankName: bank.name,
    mappingSource: aliases[normalized] === bin ? 'alias' : 'misa',
    confidence: 1,
  };
}

export async function resolveBankName(
  sourceBankName?: string,
  options?: { allowAi?: boolean },
): Promise<ResolvedBank | null> {
  const source = (sourceBankName || '').replace(/[\r\n\t]+/g, ' ').trim().slice(0, 180);
  if (!source) return null;
  const settings = await getSettings();
  const deterministic = resolveBankNameDeterministically(source, settings.bankAliases || {});
  if (deterministic) return deterministic;
  if (options?.allowAi === false) return null;

  const result = await resolveBankWithAI(source, BANKS);
  const candidate = bankByBin(String(result.matchedBin));
  const threshold = settings.bankMappingConfidenceThreshold ?? 0.6;
  const valid =
    result.sourceBankName === source &&
    !!candidate &&
    normalizeBankName(result.matchedBankName) === normalizeBankName(candidate.name) &&
    Number.isFinite(Number(result.confidence)) &&
    Number(result.confidence) >= threshold;
  if (!valid) return null;

  const normalized = normalizeBankName(source);
  await saveSettings({
    ...settings,
    bankAliases: { ...(settings.bankAliases || {}), [normalized]: candidate!.bin },
  });
  return {
    bankBin: candidate!.bin,
    bankName: candidate!.name,
    mappingSource: 'ai',
    confidence: Number(result.confidence),
  };
}
